import type { SantaChatTier, SantaGesture } from '@/server/domain/santa-chat'
import type { SantaChatMessageDto, SantaChatOpeningDto, SantaChatReplyTurnDto } from './api'

export type Delivery = 'sending' | 'delivered' | 'failed'
export type CallCtaReason = 'natural' | 'limit'
/** El chat no acepta mensajes hasta que la apertura llega bien. */
export type OpeningStatus = 'pending' | 'ready' | 'failed'

export interface ChatEntry {
  id: string
  author: 'child' | 'santa'
  text: string
  inputMode: 'voice' | 'text' | null
  gesture: SantaGesture | null
  flagged: boolean
  createdAt: string
  delivery: Delivery
  /** Segundos de la nota cuando se conocen (grabación del niño, voz ya cargada de Papá Noel). */
  durationSeconds?: number
  /**
   * Nota fallida que se puede volver a enviar tal cual (red, servidor, descanso,
   * los elfos). Un «no te he oído» no: reenviar el mismo audio daría lo mismo.
   */
  retryable?: boolean
}

export interface ChatState {
  entries: ChatEntry[]
  /** Entre soltar el micrófono y la respuesta: «Papá Noel está grabando…». */
  santaRecording: boolean
  gesture: SantaGesture
  /** Cambia en cada gesto nuevo para reiniciar su clip aunque se repita el gesto. */
  gestureTurn: number
  limitReached: boolean
  /** Instante (ISO) en que vuelve el micrófono tras el tope del día; lo calcula el servidor. */
  limitResetsAt: string | null
  callCta: CallCtaReason | null
  /** Nivel de la cuenta (lo dice el servidor); `null` hasta abrir. Con `free`, el tope ofrece los packs. */
  tier: SantaChatTier | null
  playingId: string | null
  /** Nota cuya voz (no la risa) está sonando: el panel le enseña hablando. */
  speakingId: string | null
  /** Nota cuya voz se está pidiendo al servidor para repetirla: su ▶ enseña que carga. */
  loadingId: string | null
  /** Mensaje cuya voz bloqueó el navegador: se ofrece «▶ Escuchar». */
  blockedId: string | null
  opening: OpeningStatus
  /** Tras un RATE_LIMITED: instante (ISO) hasta el que Papá Noel descansa. */
  restUntil: string | null
  /** Lo que dijo Papá Noel en un turno que no se guardó («no te he oído», «los elfos…»): aviso, no historial. */
  fallbackNotice: FallbackNotice | null
}

/** `id` es el de su reproducción: si el navegador bloquea esa voz, el aviso es lo que se vuelve a escuchar. */
export interface FallbackNotice {
  id: string
  /** `elvesError` se puede reenviar; `didNotHear`, no. */
  reason: 'didNotHear' | 'elvesError'
  text: string
  gesture: SantaGesture
}

/**
 * La apertura tal y como la entiende el chat, sin saber de qué transporte
 * viene: las notas ya cargadas (historial y saludo), el tope y el nivel. El
 * chat del niño la saca de su API (`chatOpeningOf`); el público, de la suya.
 */
export interface ChatOpening {
  entries: ChatEntry[]
  limitReached: boolean
  limitResetsAt: string | null
  /** `null` cuando el transporte no tiene niveles (el chat público). */
  tier: SantaChatTier | null
}

/** Un turno guardado, sin saber de qué transporte viene: las dos notas y lo que cambia del chat. */
export interface ChatTurn {
  child: ChatEntry
  santa: ChatEntry
  limitReached: boolean
  limitResetsAt: string | null
  showCallCta: boolean
  /** La respuesta de Papá Noel es la de seguridad (buscar a un adulto): no se ofrece llamar. */
  safety: boolean
  tier: SantaChatTier | null
}

export type ChatAction =
  | { type: 'opened'; opening: ChatOpening }
  /** Una sesión que ya estaba abierta (recargar la página): el chat sigue sin pedir otra apertura. */
  | { type: 'resumed' }
  | { type: 'childSent'; entry: ChatEntry }
  | { type: 'turnArrived'; localId: string; turn: ChatTurn }
  /** Turno sin guardar: la burbuja del niño queda fallida y lo de Papá Noel, como aviso. */
  | { type: 'turnFallback'; localId: string; notice: FallbackNotice }
  | { type: 'turnFailed'; localId: string }
  /** El niño toca «volver a enviar»: la burbuja fallida se va y el reenvío entra como una nueva. */
  | { type: 'resendStarted'; localId: string }
  | { type: 'limitHit'; localId: string; resetsAt: string | null }
  /** Repetir una nota cuya voz no está en memoria: se pide al servidor. */
  | { type: 'voiceLoading'; id: string }
  | { type: 'playing'; id: string; gesture: SantaGesture }
  /** Tras la risa (si la hay), empieza su voz. */
  | { type: 'voiceStarted'; id: string }
  | { type: 'blocked'; id: string }
  | { type: 'playbackEnded' }
  | { type: 'durationKnown'; id: string; seconds: number }
  | { type: 'historyCleared' }
  | { type: 'openFailed' }
  | { type: 'openRetried' }
  /** RATE_LIMITED al enviar (`localId`: su burbuja queda fallida) o al repetir una nota. */
  | { type: 'rested'; localId?: string; until: string }
  /** Papá Noel dice algo fuera de un turno (la despedida del chat público): una nota más al final. */
  | { type: 'santaSaid'; entry: ChatEntry }

export const INITIAL_CHAT_STATE: ChatState = {
  entries: [],
  santaRecording: false,
  gesture: 'idle',
  gestureTurn: 0,
  limitReached: false,
  limitResetsAt: null,
  callCta: null,
  tier: null,
  playingId: null,
  speakingId: null,
  loadingId: null,
  blockedId: null,
  opening: 'pending',
  restUntil: null,
  fallbackNotice: null,
}

export function toChatEntry(message: SantaChatMessageDto): ChatEntry {
  return {
    id: message.id,
    author: message.author,
    text: message.text,
    inputMode: message.inputMode,
    gesture: message.gesture,
    flagged: message.flagged,
    createdAt: message.createdAt,
    delivery: 'delivered',
  }
}

function withGesture(state: ChatState, gesture: SantaGesture): ChatState {
  return { ...state, gesture, gestureTurn: state.gestureTurn + 1 }
}

/** La burbuja local pasa a ser el mensaje guardado, conservando la duración de la grabación. */
function replaceEntry(entries: ChatEntry[], localId: string, confirmed: ChatEntry): ChatEntry[] {
  return entries.map((entry) =>
    entry.id === localId ? { ...confirmed, durationSeconds: entry.durationSeconds } : entry
  )
}

function markFailed(entries: ChatEntry[], localId: string, retryable: boolean): ChatEntry[] {
  return entries.map((entry) => (entry.id === localId ? { ...entry, delivery: 'failed', retryable } : entry))
}

function ctaAfterTurn(state: ChatState, turn: ChatTurn): CallCtaReason | null {
  // A safety reply ("tell a trusted adult") never sits next to a call invitation, not even an earlier one.
  if (turn.safety) return null
  if (!turn.showCallCta) return state.callCta
  return turn.limitReached ? 'limit' : 'natural'
}

type Handlers = {
  [K in ChatAction['type']]: (state: ChatState, action: Extract<ChatAction, { type: K }>) => ChatState
}

/** El saludo se guarda antes de leer el historial, así que suele venir ya dentro. */
function openingMessages({ history, greeting }: SantaChatOpeningDto): SantaChatMessageDto[] {
  if (!greeting || history.some((message) => message.id === greeting.message.id)) return history
  return [...history, greeting.message]
}

/** La apertura de la API del chat del niño, en la forma del chat. */
export function chatOpeningOf(opening: SantaChatOpeningDto): ChatOpening {
  return {
    entries: openingMessages(opening).map(toChatEntry),
    limitReached: opening.limitReached,
    limitResetsAt: opening.limitResetsAt,
    tier: opening.tier,
  }
}

/** Un turno de la API del chat del niño, en la forma del chat. */
export function chatTurnOf(turn: SantaChatReplyTurnDto): ChatTurn {
  return {
    child: toChatEntry(turn.childMessage),
    santa: toChatEntry(turn.santaMessage),
    limitReached: turn.limitReached,
    limitResetsAt: turn.limitResetsAt,
    showCallCta: turn.showCallCta,
    safety: turn.safety,
    tier: turn.tier,
  }
}

/** Lo que el niño haya enviado mientras se abría el chat se queda detrás de lo cargado. */
function mergeOpening(entries: ChatEntry[], opening: ChatOpening): ChatEntry[] {
  const loadedIds = new Set(opening.entries.map((entry) => entry.id))
  return [...opening.entries, ...entries.filter((entry) => !loadedIds.has(entry.id))]
}

/** Envío que no llegó a guardarse: su burbuja queda fallida y Papá Noel vuelve al reposo. */
function failSend(state: ChatState, localId: string, retryable = true): ChatState {
  return withGesture(
    { ...state, entries: markFailed(state.entries, localId, retryable), santaRecording: false },
    'idle'
  )
}

/** Un envío rechazado por ritmo deja su burbuja fallida; una repetición rechazada no toca nada más. */
function rest(state: ChatState, localId: string | undefined, until: string): ChatState {
  const rested = { ...state, restUntil: until, loadingId: null }
  return localId === undefined ? rested : failSend(rested, localId)
}

const HANDLERS: Handlers = {
  opened: (state, { opening }) => ({
    ...state,
    opening: 'ready',
    entries: mergeOpening(state.entries, opening),
    // Un tope que ya llegó (un 429 mientras se abría) no se deshace.
    limitReached: state.limitReached || opening.limitReached,
    limitResetsAt: state.limitResetsAt ?? opening.limitResetsAt,
    callCta: opening.limitReached ? 'limit' : state.callCta,
    tier: opening.tier,
  }),
  resumed: (state) => (state.opening === 'pending' ? { ...state, opening: 'ready' } : state),
  childSent: (state, { entry }) =>
    withGesture({ ...state, entries: [...state.entries, entry], santaRecording: true, fallbackNotice: null }, 'think'),
  turnArrived: (state, { localId, turn }) => ({
    ...state,
    entries: [...replaceEntry(state.entries, localId, turn.child), turn.santa],
    santaRecording: false,
    restUntil: null,
    limitReached: turn.limitReached,
    limitResetsAt: turn.limitResetsAt,
    callCta: ctaAfterTurn(state, turn),
    tier: turn.tier,
  }),
  turnFallback: (state, { localId, notice }) => ({
    ...failSend(state, localId, notice.reason === 'elvesError'),
    restUntil: null,
    fallbackNotice: notice,
  }),
  turnFailed: (state, { localId }) => failSend(state, localId),
  resendStarted: (state, { localId }) => ({
    ...state,
    entries: state.entries.filter((entry) => entry.id !== localId),
  }),
  limitHit: (state, { localId, resetsAt }) =>
    withGesture(
      {
        ...state,
        entries: state.entries.filter((entry) => entry.id !== localId),
        santaRecording: false,
        limitReached: true,
        limitResetsAt: resetsAt,
        callCta: 'limit',
      },
      'idle'
    ),
  voiceLoading: (state, { id }) => withGesture({ ...state, loadingId: id, playingId: null, speakingId: null }, 'idle'),
  playing: (state, { id, gesture }) =>
    withGesture({ ...state, playingId: id, speakingId: null, loadingId: null, blockedId: null, restUntil: null }, gesture),
  voiceStarted: (state, { id }) => ({ ...state, speakingId: id }),
  blocked: (state, { id }) =>
    withGesture({ ...state, playingId: null, speakingId: null, loadingId: null, blockedId: id }, 'idle'),
  playbackEnded: (state) => withGesture({ ...state, playingId: null, speakingId: null, loadingId: null }, 'idle'),
  durationKnown: (state, { id, seconds }) => ({
    ...state,
    entries: state.entries.map((entry) => (entry.id === id ? { ...entry, durationSeconds: seconds } : entry)),
  }),
  historyCleared: (state) =>
    withGesture(
      { ...state, entries: [], playingId: null, speakingId: null, loadingId: null, blockedId: null, fallbackNotice: null },
      'idle'
    ),
  openFailed: (state) => ({ ...state, opening: 'failed' }),
  openRetried: (state) => ({ ...state, opening: 'pending' }),
  rested: (state, { localId, until }) => rest(state, localId, until),
  santaSaid: (state, { entry }) => ({ ...state, entries: [...state.entries, entry] }),
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  const handle = HANDLERS[action.type] as (state: ChatState, action: ChatAction) => ChatState
  return handle(state, action)
}

/** Índice (1, 2, 3…) del próximo mensaje del niño hoy, para la analítica. */
export function nextChildIndexToday(entries: ChatEntry[], now: Date): number {
  const today = now.toDateString()
  const sentToday = entries.filter(
    (entry) =>
      entry.author === 'child' &&
      entry.delivery !== 'failed' &&
      new Date(entry.createdAt).toDateString() === today
  )
  return sentToday.length + 1
}
